import { watch, onScopeDispose, type Ref } from 'vue';
import { WEBSOCKET_CONFIG } from '@/config/constants';
import type { AppGameState } from './useGameFlow';
import type { ConnectionStatus } from './webSocketSingleton';

/**
 * Whether a dead socket in this phase means the game cannot continue, and the
 * app should give up on it once CONNECTION_GIVE_UP_MS elapses. A total Record
 * so a new phase has to be classified at compile time rather than silently
 * defaulting either way.
 *
 * 'match-found' is in scope because nothing else covers it: GameMatchmaking's
 * error watcher handles the searching states but not the 'match-found' it sets
 * itself, so a drop during the countdown would otherwise sit on "Starting in
 * 1 second..." forever. GameMatchmaking holds the socket reference through that
 * state, so the reconnect attempts the window waits on actually run.
 *
 * 'game-over' is out of scope: the summary needs no socket, and pulling a
 * player off their results after two seconds of trouble destroys UI that reads
 * perfectly well offline. 'matchmaking' and 'error' are not live games.
 */
const GIVES_UP_ON_CONNECTION: Record<AppGameState, boolean> = {
  matchmaking: false,
  'match-found': true,
  'selecting-letters': true,
  'in-progress': true,
  'round-finished': true,
  'awaiting-new-round': true,
  'game-over': false,
  error: false,
};

export interface UseConnectionGiveUpOptions {
  gameState: Readonly<Ref<AppGameState>>;
  connectionStatus: Readonly<Ref<ConnectionStatus>>;
  /** Called once the window elapses with the socket still down. */
  onGiveUp: () => void;
}

/**
 * In-game connection tolerance: a single window, and the sole authority on when
 * the app stops waiting for the socket.
 *
 * Nothing is rendered while it runs. The case worth protecting is pod
 * switchover on a deploy. It recovers in ~200-450 ms when the socket's first
 * retry lands, and by ~1.95 s when it does not and the shutdown ladder works
 * through its later rungs — the last of which starts by 1650 ms and is allowed
 * CONNECT_HANDSHAKE_BUDGET_MS to connect. Showing and then retracting a surface
 * inside a window that short reads worse than showing nothing at all. When the
 * window does elapse the player goes home with a notice, which reads as a lag
 * spike followed by an explanation.
 *
 * The trigger is connection status alone. Reconnect-budget exhaustion is not a
 * second trigger — and since #510 it is no longer true that it *could* not be:
 * a shutdown episode's last attempt starts by 1650 ms and a fast-failing one
 * closes ~150 ms later, so the budget is now spent before this window elapses
 * rather than long after it.
 *
 * The two are ordered by design rather than by arithmetic. Exhaustion only sets
 * connectionStatus to 'error', which this window is already watching, so it
 * changes nothing about when the player is told. The gap between them is only
 * about 200 ms — exhaustion at ~1800 ms against this 2000 ms window — and on
 * the path that matters the last attempt is still shaking hands through it.
 * Adding exhaustion as a second trigger would trade that path away for a
 * fraction of a second of promptness on the path where the game is already
 * lost.
 */
export function useConnectionGiveUp(options: UseConnectionGiveUpOptions): void {
  const { gameState, connectionStatus, onGiveUp } = options;

  const shouldGiveUp = () =>
    GIVES_UP_ON_CONNECTION[gameState.value] && connectionStatus.value !== 'connected';

  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const clearWindow = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  watch(
    shouldGiveUp,
    pending => {
      if (!pending) {
        clearWindow();
        return;
      }
      if (timeoutId !== null) return;
      timeoutId = setTimeout(() => {
        timeoutId = null;
        // Re-check at fire time rather than trusting the arming edge: a very
        // fast reconnect can resolve without the watcher observing the false
        // transition.
        if (shouldGiveUp()) {
          onGiveUp();
        }
      }, WEBSOCKET_CONFIG.CONNECTION_GIVE_UP_MS);
    },
    { immediate: true }
  );

  onScopeDispose(clearWindow);
}
